import { Container } from '@/components/Container';
import { useTranslation } from '@/hooks/useTranslation';
import { useAnalytics } from '@/hooks/useAnalytics';
import { getWhatsAppUrl } from '@/data/loader';

/* ═══════════════════════════════════════════
   QUANTO CUSTA — Orçamento por perfil
   Dados reais do JSON + pesquisa de campo
   ═══════════════════════════════════════════ */

interface Perfil {
  id: string;
  total: string;
  cor: string;
  corBg: string;
  corTexto: string;
  itens: { key: string; valor: string }[];
  isMostPopular?: boolean;
}

const perfis: Perfil[] = [
  {
    id: 'premium',
    total: 'R$ 2.500–4.000',
    cor: 'border-amber-400',
    corBg: 'bg-amber-50',
    corTexto: 'text-amber-700',
    itens: [
      { key: 'guide.costs.item.accommodation', valor: 'R$ 1.000–1.600' },
      { key: 'guide.costs.item.food', valor: 'R$ 450–750' },
      { key: 'guide.costs.item.tours', valor: 'R$ 200–400' },
      { key: 'guide.costs.item.transport', valor: 'R$ 200–500' },
    ],
  },
  {
    id: 'comfort',
    total: 'R$ 1.200–2.000',
    cor: 'border-teal-400',
    corBg: 'bg-teal-50',
    corTexto: 'text-teal-700',
    isMostPopular: true,
    itens: [
      { key: 'guide.costs.item.accommodation', valor: 'R$ 560–800' },
      { key: 'guide.costs.item.food', valor: 'R$ 300–500' },
      { key: 'guide.costs.item.tours', valor: 'R$ 150–300' },
      { key: 'guide.costs.item.transport', valor: 'R$ 150–300' },
    ],
  },
  {
    id: 'backpacker',
    total: 'R$ 600–1.000',
    cor: 'border-sand-300',
    corBg: 'bg-sand-50',
    corTexto: 'text-warm-dark',
    itens: [
      { key: 'guide.costs.item.accommodation', valor: 'R$ 160–300' },
      { key: 'guide.costs.item.food', valor: 'R$ 180–300' },
      { key: 'guide.costs.item.tours', valor: 'R$ 0–80' },
      { key: 'guide.costs.item.transport', valor: 'R$ 120–200' },
    ],
  },
];

const tabelaDetalhada = [
  {
    item: 'guide.costs.table.bus',
    premium: '—',
    comfort: 'guide.costs.table.busPriceRound',
    backpacker: 'guide.costs.table.busPriceRound',
  },
  { item: 'guide.costs.table.fuel', premium: 'R$ 100–150', comfort: 'R$ 80–120', backpacker: '—' },
  { item: 'guide.costs.table.parking', premium: 'R$ 80', comfort: 'R$ 50–80', backpacker: '—' },
  {
    item: 'guide.costs.table.ferry',
    premium: 'R$ 44,18',
    comfort: 'R$ 44,18',
    backpacker: 'R$ 44,18',
  },
  {
    item: 'guide.costs.item.accommodation',
    premium: 'R$ 1.000–1.600',
    comfort: 'R$ 560–800',
    backpacker: 'R$ 160–300',
  },
  {
    item: 'guide.costs.item.food',
    premium: 'R$ 450–750',
    comfort: 'R$ 300–500',
    backpacker: 'R$ 180–300',
  },
  {
    item: 'guide.costs.item.tours',
    premium: 'R$ 200–400',
    comfort: 'R$ 150–300',
    backpacker: 'R$ 0–80',
  },
];

const dicas = [
  {
    icon: '🍺',
    ariaLabel: 'Cerveja',
    title: 'guide.costs.tips.beer.title',
    desc: 'guide.costs.tips.beer.desc',
  },
  {
    icon: '🍽️',
    ariaLabel: 'Prato de comida',
    title: 'guide.costs.tips.buffet.title',
    desc: 'guide.costs.tips.buffet.desc',
  },
  {
    icon: '💰',
    ariaLabel: 'Dinheiro',
    title: 'guide.costs.tips.cash.title',
    desc: 'guide.costs.tips.cash.desc',
  },
  {
    icon: '🛒',
    ariaLabel: 'Carrinho de compras',
    title: 'guide.costs.tips.groceries.title',
    desc: 'guide.costs.tips.groceries.desc',
  },
];

const whatsappSvg = (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export function QuantoCustaSection() {
  const { t } = useTranslation();
  const { trackWhatsapp } = useAnalytics();

  return (
    <section id="quanto-custa" className="py-16 sm:py-20 bg-white">
      <Container>
        {/* Header */}
        <div className="text-center mb-12">
          <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
            {t('guide.costs.overline')}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl text-warm-dark mt-2">
            {t('guide.costs.title')}
          </h2>
          <p className="font-editorial text-lg text-warm-gray mt-2 max-w-2xl mx-auto">
            {t('guide.costs.subtitle')}
          </p>
        </div>

        {/* Cards de perfil */}
        <div className="grid md:grid-cols-3 gap-5 mb-8">
          {perfis.map(p => (
            <div
              key={p.id}
              className={`rounded-2xl border-2 ${p.cor} ${p.corBg} p-6 flex flex-col`}
            >
              {/* Header do card */}
              <div className="mb-4">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-body text-xs font-bold uppercase tracking-wider text-warm-gray">
                    {t(`guide.costs.profile.${p.id}.subtitle`)}
                  </span>
                  {p.isMostPopular && (
                    <span className="bg-teal-deep/10 text-teal-deep text-xs font-body font-bold px-2 py-0.5 rounded-full border border-teal-deep/20">
                      Mais popular
                    </span>
                  )}
                </div>
                <h3 className="font-display text-2xl text-warm-dark mt-1">
                  {t(`guide.costs.profile.${p.id}.name`)}
                </h3>
              </div>

              {/* Preço no topo — destaque */}
              <div className={`${p.corBg.replace('50', '100')} rounded-xl p-4 text-center mb-5`}>
                <span className="font-body text-xs text-warm-gray">A partir de</span>
                <div className={`font-display text-3xl ${p.corTexto} mt-1`}>{p.total}</div>
                <span className="font-body text-xs text-warm-gray">
                  {t('guide.costs.perPerson')}
                </span>
              </div>

              {/* Lista de itens */}
              <div className="space-y-3 flex-1">
                {p.itens.map(item => (
                  <div key={item.key} className="flex justify-between items-baseline gap-2">
                    <span className="font-body text-sm text-warm-gray">{t(item.key)}</span>
                    <span className="font-body text-sm font-semibold text-warm-dark whitespace-nowrap">
                      {item.valor}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Callout sazonal */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 mb-8">
          <span role="img" aria-label="Calendário" className="text-xl flex-shrink-0">
            📅
          </span>
          <div>
            <p className="font-body text-sm font-semibold text-warm-dark">
              Variação sazonal de preços
            </p>
            <p className="font-body text-sm text-warm-gray">
              Na alta temporada (jan–mar, jul, dez), espere pagar <strong>40–70% a mais</strong> em
              acomodações e passeios. Reserve com antecedência — pousadas lotam meses antes no
              Carnaval e Réveillon.
            </p>
          </div>
        </div>

        {/* Tabela detalhada por item */}
        <div className="mb-16">
          <h3 className="font-display text-2xl text-warm-dark mb-6 text-center">
            {t('guide.costs.detailsTitle')}
          </h3>
          <div className="relative mb-16">
            <div className="overflow-x-auto">
              <table className="w-full font-body text-sm min-w-[480px]">
                <thead>
                  <tr className="border-b-2 border-sand-200">
                    <th className="text-left py-3 px-4 font-semibold text-warm-dark">
                      {t('guide.costs.table.item')}
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-warm-dark">
                      {t('guide.costs.table.premium')}
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-warm-dark">
                      {t('guide.costs.table.comfort')}
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-warm-dark">
                      {t('guide.costs.table.backpacker')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tabelaDetalhada.map((row, idx) => (
                    <tr key={idx} className="border-b border-sand-100">
                      <td className="py-3 px-4 text-warm-gray">{t(row.item)}</td>
                      <td className="py-3 px-4 text-right text-warm-dark">
                        {row.premium.startsWith('guide.') ? t(row.premium) : row.premium}
                      </td>
                      <td className="py-3 px-4 text-right text-warm-dark">
                        {row.comfort.startsWith('guide.') ? t(row.comfort) : row.comfort}
                      </td>
                      <td className="py-3 px-4 text-right text-warm-dark">
                        {row.backpacker.startsWith('guide.') ? t(row.backpacker) : row.backpacker}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-b-2 border-sand-200 font-semibold">
                    <td className="py-3 px-4 text-warm-dark">{t('guide.costs.table.total')}</td>
                    <td className="py-3 px-4 text-right text-coral">R$ 2.500–4.000</td>
                    <td className="py-3 px-4 text-right text-coral">R$ 1.200–2.000</td>
                    <td className="py-3 px-4 text-right text-coral">R$ 600–1.000</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent pointer-events-none" />
          </div>
        </div>

        {/* Dicas de ouro dos concorrentes */}
        <div className="bg-coral/5 border border-coral/20 rounded-2xl p-6 space-y-4">
          <h4 className="font-display text-xl text-warm-dark flex items-center gap-2">
            <span role="img" aria-label="Dica">
              💡
            </span>{' '}
            {t('guide.costs.tipsTitle')}
          </h4>
          <div className="grid sm:grid-cols-2 gap-4">
            {dicas.map(d => (
              <div key={d.title} className="flex gap-3">
                <span role="img" aria-label={d.ariaLabel} className="text-2xl flex-shrink-0">
                  {d.icon}
                </span>
                <div>
                  <p className="font-body text-sm font-semibold text-warm-dark">{t(d.title)}</p>
                  <p className="font-body text-sm text-warm-gray">{t(d.desc)}</p>
                </div>
              </div>
            ))}

            {/* ATM / espécie — item adicional */}
            <div className="flex gap-3">
              <span role="img" aria-label="ATM" className="text-2xl flex-shrink-0">
                🏧
              </span>
              <div>
                <p className="font-body text-sm font-semibold text-warm-dark">
                  Leve dinheiro em espécie
                </p>
                <p className="font-body text-sm text-warm-gray">
                  O único ATM fica em Nova Brasília (Bradesco, limite baixo por saque). Encantadas
                  não tem banco. Leve ao menos R$150–200/pessoa/dia em dinheiro.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* WhatsApp CTA contextual */}
        <div className="mt-8 text-center">
          <p className="font-body text-sm text-warm-gray mb-3">
            Quer ajuda para montar seu orçamento?
          </p>
          <a
            href={getWhatsAppUrl(
              '41999999999',
              'Oi! Li o guia de quanto custa na Ilha do Mel e quero ajuda para montar meu orçamento:',
              'guia-quanto-custa-cta'
            )}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsapp({ provider: 'Geral', source: 'guia-quanto-custa-cta' })}
            className="inline-flex items-center gap-2.5 bg-[#25D366] hover:bg-[#20bd5a] text-white font-body font-semibold text-sm rounded-xl px-6 py-3 min-h-[44px] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
          >
            {whatsappSvg}
            Fala com a gente no WhatsApp
          </a>
        </div>
      </Container>
    </section>
  );
}
